import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api, type JsonObject } from "../api/client";
import { i18n } from "../i18n";
import "./a2k-autocomplete/a2k-autocomplete";

export interface ReferenceOption extends JsonObject {
  id: string;
  value: string;
  label: string;
  description?: string;
}

export function referenceValues(value: unknown): string[] {
  return (Array.isArray(value) ? value : value ? [value] : [])
    .map((item) =>
      String(
        typeof item === "object" && item
          ? (item as JsonObject).value ||
              (item as JsonObject).repository_key ||
              (item as JsonObject).id ||
              ""
          : item,
      ),
    )
    .filter(Boolean);
}
export function withLocation(record: JsonObject): JsonObject {
  const value = { ...record };
  if (value.location) delete value.location_id;
  else delete value.location;
  return value;
}

/** Only selected API values leave this control; search labels never become IDs. */
@customElement("knowledge-reference")
export class KnowledgeReference extends LitElement {
  @property() resource = "";
  @property() repository = "";
  @property() namespace = "";
  @property() fileId = "";
  @property() label = "";
  @property({ type: Boolean }) multiple = false;
  @property({ type: Boolean }) required = false;
  @property({ attribute: false }) values: string[] = [];
  @state() private query = "";
  @state() private options: ReferenceOption[] = [];
  @state() private selected = new Map<string, ReferenceOption>();
  @state() private error = "";
  @state() private busy = false;
  @state() private open = false;
  @state() private active = -1;
  private sequence = 0;
  private resolveSequence = 0;
  private timer: ReturnType<typeof setTimeout> | undefined;

  static styles = css`
    :host {
      display: block;
      min-width: 0;
    }
    input {
      width: 100%;
      box-sizing: border-box;
      padding: 6px;
      border: 2px inset #fff;
      font: inherit;
    }
    .selected {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
      margin-bottom: 4px;
    }
    .selected button {
      max-width: 100%;
      overflow-wrap: anywhere;
    }
    .options {
      max-height: 220px;
      overflow: auto;
      border: 2px inset #fff;
    }
    .option {
      display: grid;
      width: 100%;
      text-align: left;
      background: #fff;
      border: 0;
      padding: 7px;
      cursor: pointer;
    }
    .option:hover,
    .option[aria-selected="true"] {
      background: #000080;
      color: white;
    }
    small {
      overflow-wrap: anywhere;
    }
    .error {
      color: #8b1717;
    }
  `;

  protected updated(changed: Map<PropertyKey, unknown>) {
    const scopeChanged = ["resource", "repository", "namespace", "fileId"].some(
      (key) => changed.has(key),
    );
    if (scopeChanged) {
      this.sequence += 1;
      this.options = [];
      this.selected = new Map();
      if (this.open) void this.search();
    }
    if (scopeChanged || changed.has("values")) void this.resolveSelected();
  }
  disconnectedCallback() {
    this.sequence += 1;
    this.resolveSequence += 1;
    clearTimeout(this.timer);
    super.disconnectedCallback();
  }
  private async optionsFor(q: string) {
    const scope: Record<string, string> = {};
    const entities = ["file", "location", "symbol", "contract", "evidence"];
    if ([...entities, "component", "namespace"].includes(this.resource))
      scope.repository = this.repository;
    if ([...entities, "namespace"].includes(this.resource))
      scope.namespace = this.namespace;
    if (entities.includes(this.resource)) scope.file_id = this.fileId;
    const response = await api.knowledgeReferences({
      resource: this.resource,
      q,
      ...scope,
      limit: 20,
    });
    return response.items as ReferenceOption[];
  }
  private async resolveSelected() {
    const sequence = ++this.resolveSequence;
    const scope = [
      this.resource,
      this.repository,
      this.namespace,
      this.fileId,
    ].join("|");
    for (const value of this.values) {
      if (this.selected.has(value)) continue;
      try {
        const items = await this.optionsFor(value);
        if (
          sequence !== this.resolveSequence ||
          scope !==
            [this.resource, this.repository, this.namespace, this.fileId].join(
              "|",
            ) ||
          !this.values.includes(value)
        )
          continue;
        const selected = items.find((item) => item.value === value);
        if (selected)
          this.selected = new Map(this.selected).set(value, selected);
      } catch {
        /* Existing IDs remain intact when a read-only label lookup fails. */
      }
    }
  }
  private async search() {
    if (this.resource === "component" && !this.repository) {
      this.options = [];
      return;
    }
    const sequence = ++this.sequence;
    this.busy = true;
    this.error = "";
    try {
      const options = await this.optionsFor(this.query.trim());
      if (sequence === this.sequence) {
        this.options = options;
        this.active = -1;
      }
    } catch (error) {
      if (sequence === this.sequence) {
        this.options = [];
        this.error = (error as Error).message;
      }
    } finally {
      if (sequence === this.sequence) this.busy = false;
    }
  }
  private emit(values: string[]) {
    this.values = values;
    this.dispatchEvent(
      new CustomEvent<string[]>("reference-change", {
        detail: values,
        bubbles: true,
        composed: true,
      }),
    );
  }
  private choose(option: ReferenceOption) {
    this.selected = new Map(this.selected).set(option.value, option);
    this.query = "";
    this.open = false;
    this.emit(
      this.multiple
        ? [...new Set([...this.values, option.value])]
        : [option.value],
    );
  }
  reportValidity(): boolean {
    const input = this.renderRoot.querySelector("input");
    const valid =
      !this.query.trim() && (!this.required || this.values.length > 0);
    input?.setCustomValidity(valid ? "" : i18n.t("knowledgeChooseReference"));
    if (!valid) input?.reportValidity();
    return valid;
  }
  private keydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      this.open = false;
      return;
    }
    if (["ArrowDown", "ArrowUp"].includes(event.key) && this.options.length) {
      event.preventDefault();
      this.open = true;
      this.active =
        (this.active +
          (event.key === "ArrowDown" ? 1 : -1) +
          this.options.length) %
        this.options.length;
    } else if (event.key === "Enter" && this.open && this.active >= 0) {
      event.preventDefault();
      this.choose(this.options[this.active]);
    }
  }
  render() {
    return html`<div class="selected">
        ${this.values.map(
          (value) =>
            html` <button
              type="button"
              title=${i18n.t("remove")}
              @click=${() => this.emit(this.values.filter((item) => item !== value))}
            >
              ${this.selected.get(value)?.label || value} ×
            </button>`,
        )}
      </div>
      <input
        role="combobox"
        aria-label=${this.label}
        aria-autocomplete="list"
        aria-expanded=${String(this.open)}
        aria-controls="reference-options"
        aria-activedescendant=${this.active >= 0 ? `reference-${this.active}` : nothing}
        ?disabled=${this.resource === "component" && !this.repository}
        placeholder=${i18n.t(this.resource === "component" && !this.repository ? "knowledgeChooseRepositoryFirst" : "knowledgeChooseReference")}
        .value=${this.query}
        @focus=${() => {
          this.open = true;
          void this.search();
        }}
        @keydown=${this.keydown}
        @input=${(event: Event) => {
          this.query = (event.target as HTMLInputElement).value;
          this.open = true;
          this.sequence += 1;
          this.options = [];
          clearTimeout(this.timer);
          this.timer = setTimeout(() => void this.search(), 200);
        }}
      />
      ${
        this.open
          ? html`<div class="options" id="reference-options" role="listbox">
              ${this.busy ? html`<small>${i18n.t("loading")}</small>` : nothing}
              ${this.error ? html`<small class="error">${this.error}</small>` : nothing}
              ${!this.busy && !this.error && !this.options.length ? html`<small>${i18n.t("empty")}</small>` : nothing}
              ${this.options.map(
                (option, index) =>
                  html`<button
                    type="button"
                    class="option"
                    id=${`reference-${index}`}
                    role="option"
                    aria-selected=${String(index === this.active)}
                    @click=${() => this.choose(option)}
                  >
                    <strong>${option.label}</strong>
                    <small>${option.description || option.value}</small>
                  </button>`,
              )}
            </div>`
          : nothing
      }`;
  }
}

/** Paths may be new; unlike entity references their literal text is the value. */
@customElement("knowledge-path-input")
export class KnowledgePathInput extends LitElement {
  @property() value = "";
  @property() namespace = "";
  @property() label = "";
  @state() private paths: string[] = [];
  private sequence = 0;
  static styles = css`
    :host {
      display: block;
      min-width: 0;
    }
  `;
  protected updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has("namespace")) {
      this.paths = [];
      void this.suggest();
    }
  }
  disconnectedCallback() {
    this.sequence += 1;
    super.disconnectedCallback();
  }
  private async suggest() {
    const sequence = ++this.sequence;
    if (!this.namespace) {
      this.paths = [];
      return;
    }
    try {
      const response = await api.knowledgeList("files", {
        namespace: this.namespace,
        q: this.value,
        page_size: 20,
      });
      if (sequence === this.sequence)
        this.paths = response.items
          .map((item) => String(item.relative_path || ""))
          .filter(Boolean);
    } catch {
      if (sequence === this.sequence) this.paths = [];
    }
  }
  private change(value: string) {
    this.value = value;
    this.dispatchEvent(
      new CustomEvent("path-change", {
        detail: value,
        bubbles: true,
        composed: true,
      }),
    );
    void this.suggest();
  }
  render() {
    return html`<a2k-autocomplete
      .value=${this.value}
      .options=${this.paths}
      .ariaLabel=${this.label}
      @focusin=${() => void this.suggest()}
      @value-change=${(event: CustomEvent<string>) => this.change(event.detail)}
    ></a2k-autocomplete>`;
  }
}
